/* Variant A: Dashboard Grid
   Wide layout using shadcn Card, Badge, Progress, Chart (Recharts),
   and Separator for all analytics sections. */

import { useState, useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from "@/components/ui/chart";
import {
  Bar,
  BarChart,
  XAxis,
  YAxis,
  Pie,
  PieChart,
  Cell,
} from "recharts";
import type { AnalyticsData } from "./analyticsFixtures";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { KiwiCollectionGrid } from "@/components/brand/KiwiCollectionGrid";
import type { KiwiSkinConfig } from "@/types/kiwiSkin";
import { useUserSettings } from "@/hooks/useUserSettings";
import { Button } from "@/components/ui/button";
import {
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronUp,
  Clock,
  Flame,
  PlayCircle,
  Calendar,
  FastForward,
} from "lucide-react";

interface AnalyticsVariantAProps {
  data: AnalyticsData;
  hideKiwiCollection?: boolean;
}

function formatMinutes(min: number): string {
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function daysAgo(ts: number): string {
  const diff = Math.round((Date.now() - ts) / 86_400_000);
  if (diff === 0) return "today";
  if (diff === 1) return "yesterday";
  return `${diff} days ago`;
}

function HeroSection({ data }: { data: AnalyticsData }) {
  const { weeklyComparison, overview, activityBounds } = data;
  const isUp = weeklyComparison.minutesChange > 0;

  return (
    <Card size="sm">
      <CardContent className="text-center py-6 space-y-2">
        <CardDescription className="uppercase tracking-widest text-xs">This week</CardDescription>
        <p className="text-4xl font-bold tracking-tight">{formatMinutes(weeklyComparison.thisWeek.minutes)}</p>
        <p className="text-sm text-muted-foreground">{weeklyComparison.thisWeek.items} items completed</p>
        <Badge variant={isUp ? "secondary" : "outline"} className="gap-1">
          {isUp ? <ArrowUp className="size-2.5" /> : <ArrowDown className="size-2.5" />}
          {Math.abs(weeklyComparison.minutesChange)}% vs last week
        </Badge>
        <div className="flex justify-center gap-4 mt-3 text-[11px] text-muted-foreground">
          <span>{formatMinutes(overview.thisMonth.minutes)} this month</span>
          <span>{overview.feedSize} in feed</span>
        </div>
        <p className="text-[10px] text-muted-foreground">
          Active since {formatDate(activityBounds.firstCompletion)} · Last active {daysAgo(activityBounds.lastCompletion)}
        </p>
      </CardContent>
    </Card>
  );
}

function StatCard({
  label,
  value,
  sub,
  icon: Icon,
}: {
  label: string;
  value: string;
  sub?: string;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <Card size="sm">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardDescription className="text-xs">{label}</CardDescription>
          <Icon className="size-3.5 text-muted-foreground" />
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-xl font-semibold tracking-tight">{value}</p>
        {sub && (
          <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>
        )}
      </CardContent>
    </Card>
  );
}

const dailyChartConfig = {
  items: { label: "Items", color: "var(--chart-1)" },
} satisfies ChartConfig;

function DailyActivityChart({ data }: { data: AnalyticsData["dailyActivity"] }) {
  const chartData = data.map((d) => ({
    date: d.date.slice(5),
    items: d.items,
  }));

  return (
    <Card size="sm">
      <CardHeader>
        <CardTitle className="text-sm">Daily Activity</CardTitle>
        <CardDescription>Items completed per day</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={dailyChartConfig} className="h-48 w-full">
          <BarChart data={chartData}>
            <XAxis dataKey="date" tickLine={false} axisLine={false} fontSize={10} />
            <YAxis tickLine={false} axisLine={false} fontSize={10} width={20} />
            <ChartTooltip content={<ChartTooltipContent />} />
            <Bar dataKey="items" fill="var(--color-items)" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}

const hourlyChartConfig = {
  count: { label: "Items", color: "var(--chart-3)" },
} satisfies ChartConfig;

function HourlyActivityChart({ data }: { data: AnalyticsData["hourlyActivity"] }) {
  const chartData = data.map((h) => {
    const label = h.hour === 0 ? "12a" : h.hour < 12 ? `${h.hour}a` : h.hour === 12 ? "12p" : `${h.hour - 12}p`;
    return { hour: label, count: h.count };
  });

  return (
    <Card size="sm">
      <CardHeader>
        <CardTitle className="text-sm">When You Consume</CardTitle>
        <CardDescription>Completions by hour of day</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={hourlyChartConfig} className="h-36 w-full">
          <BarChart data={chartData}>
            <XAxis dataKey="hour" tickLine={false} axisLine={false} fontSize={8} interval={5} />
            <ChartTooltip content={<ChartTooltipContent />} />
            <Bar dataKey="count" fill="var(--color-count)" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}

const dayOfWeekChartConfig = {
  count: { label: "Items", color: "var(--chart-2)" },
} satisfies ChartConfig;

function DayOfWeekChart({ data }: { data: AnalyticsData["dayOfWeek"] }) {
  return (
    <Card size="sm">
      <CardHeader>
        <CardTitle className="text-sm">Most Active Days</CardTitle>
      </CardHeader>
      <CardContent>
        <ChartContainer config={dayOfWeekChartConfig} className="h-36 w-full">
          <BarChart data={data}>
            <XAxis dataKey="day" tickLine={false} axisLine={false} fontSize={10} />
            <ChartTooltip content={<ChartTooltipContent />} />
            <Bar dataKey="count" fill="var(--color-count)" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}

const CONTENT_COLORS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
];

function ContentMixCard({ data }: { data: AnalyticsData["contentTypes"] }) {
  const pieData = data.map((ct, i) => ({
    name: ct.type,
    value: ct.completedMinutes,
    fill: CONTENT_COLORS[i % CONTENT_COLORS.length],
  }));

  const config = Object.fromEntries(
    data.map((ct, i) => [
      ct.type,
      { label: ct.type, color: CONTENT_COLORS[i % CONTENT_COLORS.length] },
    ])
  ) satisfies ChartConfig;

  return (
    <Card size="sm">
      <CardHeader>
        <CardTitle className="text-sm">Content Mix</CardTitle>
        <CardDescription>Time by content type</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={config} className="h-48 w-full">
          <PieChart>
            <ChartTooltip content={<ChartTooltipContent />} />
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={40}
              outerRadius={70}
              strokeWidth={2}
            >
              {pieData.map((entry) => (
                <Cell key={entry.name} fill={entry.fill} />
              ))}
            </Pie>
            <ChartLegend content={<ChartLegendContent nameKey="name" />} />
          </PieChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}

const TOP_SOURCES_LIMIT = 10;
const TOP_SOURCES_EXPANDED_HEIGHT = 320;

function SourceRow({ source }: { source: AnalyticsData["sources"][number] }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between text-xs">
        <span className="truncate">{source.name}</span>
        <Badge variant="secondary" className="text-[10px] h-4 px-1.5 shrink-0 ml-2">
          {source.completionRate.toFixed(0)}%
        </Badge>
      </div>
      <Progress value={source.completionRate} className="h-1.5" />
    </div>
  );
}

function TopSourcesCard({ data }: { data: AnalyticsData["sources"] }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const activeSources = data.filter((s) => s.completedCount > 0);
  const hasMore = activeSources.length > TOP_SOURCES_LIMIT;
  const visibleSources = isExpanded
    ? activeSources
    : activeSources.slice(0, TOP_SOURCES_LIMIT);

  return (
    <Card size="sm">
      <CardHeader>
        <CardTitle className="text-sm">Top Sources</CardTitle>
        <CardDescription>
          Completion rate per source · {activeSources.length} active
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2.5">
        {isExpanded ? (
          <ScrollArea style={{ height: TOP_SOURCES_EXPANDED_HEIGHT }}>
            <div className="space-y-2.5 pr-3">
              {visibleSources.map((source) => (
                <SourceRow key={source.name} source={source} />
              ))}
            </div>
          </ScrollArea>
        ) : (
          visibleSources.map((source) => (
            <SourceRow key={source.name} source={source} />
          ))
        )}
        {hasMore && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-xs text-muted-foreground h-7"
            onClick={() => setIsExpanded((prev) => !prev)}
          >
            {isExpanded ? (
              <>
                <ChevronUp className="size-3 mr-1" />
                Show less
              </>
            ) : (
              <>
                <ChevronDown className="size-3 mr-1" />
                Show all {activeSources.length} sources
              </>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function StreaksCard({ data }: { data: AnalyticsData["streaks"] }) {
  return (
    <Card size="sm">
      <CardHeader>
        <CardTitle className="text-sm">Streaks</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2.5">
        {data.map((streak) => (
          <div key={streak.goalName} className="flex items-center gap-2">
            <Flame className="size-3.5 text-chart-5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs truncate">{streak.goalName}</p>
              <p className="text-[10px] text-muted-foreground">
                Best: {streak.longestStreak}d
              </p>
            </div>
            <Badge variant="secondary" className="text-[10px] h-4 px-1.5">
              {streak.currentStreak}d
            </Badge>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

const goalTrendConfig = {
  minutesCompleted: { label: "Minutes", color: "var(--chart-1)" },
} satisfies ChartConfig;

function GoalTrendsCard({ data }: { data: AnalyticsData["goalTrends"] }) {
  return (
    <Card size="sm">
      <CardHeader>
        <CardTitle className="text-sm">Goal Trends (7 days)</CardTitle>
        <CardDescription>Daily progress vs goal</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {data.map((goal) => {
          const chartData = goal.days.map((d) => ({
            date: d.date.slice(5),
            minutesCompleted: d.minutesCompleted,
          }));
          return (
            <div key={goal.goalName}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium">{goal.goalName}</span>
                <Badge variant="outline" className="text-[10px] h-4 px-1.5">
                  {goal.achievementRate}% hit
                </Badge>
              </div>
              <ChartContainer config={goalTrendConfig} className="h-16 w-full">
                <BarChart data={chartData}>
                  <ChartTooltip content={<ChartTooltipContent hideLabel />} />
                  <Bar dataKey="minutesCompleted" fill="var(--color-minutesCompleted)" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ChartContainer>
              <p className="text-[9px] text-muted-foreground mt-0.5">
                Goal: {goal.dailyGoalMinutes}m/day
              </p>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function FunnelCard({ data }: { data: AnalyticsData["funnel"] }) {
  return (
    <Card size="sm">
      <CardHeader>
        <CardTitle className="text-sm">Conversion Funnel</CardTitle>
        <CardDescription>Feed to completion rates</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center gap-1">
          <div className="w-full bg-chart-1/20 rounded-md py-2 px-3 text-center">
            <p className="text-xs font-medium">Feed</p>
            <p className="text-sm font-semibold">{data.feedTotal}</p>
          </div>
          <Badge variant="outline" className="text-[10px] h-4">
            {data.feedToQueueRate}% queued
          </Badge>
          <div className="w-4/5 bg-chart-2/20 rounded-md py-2 px-3 text-center">
            <p className="text-xs font-medium">Queue</p>
            <p className="text-sm font-semibold">{data.queuedTotal}</p>
          </div>
          <Badge variant="outline" className="text-[10px] h-4">
            {data.queueToCompletedRate}% completed
          </Badge>
          <div className="w-3/5 bg-chart-3/20 rounded-md py-2 px-3 text-center">
            <p className="text-xs font-medium">Completed</p>
            <p className="text-sm font-semibold">{data.completedTotal}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const schedulingChartConfig = {
  count: { label: "Uses", color: "var(--chart-4)" },
} satisfies ChartConfig;

function SchedulingCard({ data }: { data: AnalyticsData["scheduling"] }) {
  return (
    <Card size="sm">
      <CardHeader>
        <CardTitle className="text-sm">Scheduling</CardTitle>
        <CardDescription>
          {data.completedScheduled}/{data.scheduledItems} completed ·{" "}
          {data.completionRate.toFixed(0)}% hit rate
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Progress value={data.completionRate} className="h-2 mb-3" />
        <p className="text-[10px] text-muted-foreground mb-1.5">
          Most-used time slots
        </p>
        <ChartContainer config={schedulingChartConfig} className="h-20 w-full">
          <BarChart data={data.timeSlots} layout="vertical">
            <XAxis type="number" hide />
            <YAxis dataKey="slot" type="category" tickLine={false} axisLine={false} fontSize={10} width={40} />
            <ChartTooltip content={<ChartTooltipContent />} />
            <Bar dataKey="count" fill="var(--color-count)" radius={[0, 3, 3, 0]} />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}

function QueueHealthCard({ data }: { data: AnalyticsData["queue"] }) {
  return (
    <Card size="sm">
      <CardHeader>
        <CardTitle className="text-sm">Queue Health</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-lg font-semibold">{data.totalItems}</p>
            <p className="text-[10px] text-muted-foreground">Items queued</p>
          </div>
          <div>
            <p className="text-lg font-semibold">
              {(data.averageAgeHours / 24).toFixed(1)}d
            </p>
            <p className="text-[10px] text-muted-foreground">Avg age</p>
          </div>
          <div>
            <p className="text-lg font-semibold">
              {(data.oldestAgeHours / 24).toFixed(0)}d
            </p>
            <p className="text-[10px] text-muted-foreground">Oldest item</p>
          </div>
          <div>
            <p className="text-lg font-semibold">
              {formatMinutes(data.totalMinutes)}
            </p>
            <p className="text-[10px] text-muted-foreground">Total time</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SavedTimeCard({ data }: { data: AnalyticsData["savedTime"] }) {
  return (
    <Card size="sm">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">Saved Time</CardTitle>
          <FastForward className="size-3.5 text-muted-foreground" />
        </div>
        <CardDescription>Playback at 2x speed</CardDescription>
      </CardHeader>
      <CardContent>
        <p className="text-xl font-semibold tracking-tight">
          {formatMinutes(data.savedMinutes)}
        </p>
        <p className="text-[11px] text-muted-foreground mt-0.5">
          {formatMinutes(data.contentMinutes)} of content in{" "}
          {formatMinutes(data.actualMinutes)}
        </p>
      </CardContent>
    </Card>
  );
}

function TagsCard({ data }: { data: AnalyticsData["tags"] }) {
  const totalMinutes = data.reduce((s, t) => s + t.minutes, 0);

  return (
    <Card size="sm">
      <CardHeader>
        <CardTitle className="text-sm">Time by Topic</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {data.map((t) => {
          const pct = Math.round((t.minutes / totalMinutes) * 100);
          return (
            <div key={t.tag} className="flex items-center gap-2">
              <Badge variant="outline" className="text-[10px] h-4 px-1.5 w-24 justify-center shrink-0">
                {t.tag}
              </Badge>
              <Progress value={pct} className="h-1.5 flex-1" />
              <span className="text-[10px] text-muted-foreground w-6 text-right shrink-0">
                {pct}%
              </span>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-xs font-medium uppercase tracking-widest text-muted-foreground px-1">
      {children}
    </h3>
  );
}

function KiwiCollectionSection() {
  const userSkins = useQuery(api.kiwiSkins.getUserSkins);
  const totalCount = useQuery(api.kiwiSkins.getTotalSkinCount);
  const { settings, updateSetting } = useUserSettings();

  // Optimistic local state so selection feels instant
  const serverSkinId = settings?.activeKiwiSkinId ?? "";
  const [optimisticId, setOptimisticId] = useState<string | null>(null);
  const isSaving = optimisticId !== null && optimisticId !== serverSkinId;

  // Clear optimistic state once server catches up
  const prevServerRef = useRef(serverSkinId);
  useEffect(() => {
    if (serverSkinId !== prevServerRef.current) {
      prevServerRef.current = serverSkinId;
      setOptimisticId(null);
    }
  }, [serverSkinId]);

  if (userSkins === undefined) return null;

  const displayId = optimisticId ?? serverSkinId;

  const skinConfigs = userSkins.map((s) => ({
    _id: s._id as string,
    name: s.name,
    variant: s.variant,
    palette: s.palette,
    environment: s.environment,
    artifact: s.artifact,
    effect: s.effect,
    rarity: s.rarity,
    earnedAt: s.earnedAt,
  })) as Array<KiwiSkinConfig & { _id: string; earnedAt: number }>;

  function handleSelect(skinId: string) {
    const newValue = skinId === displayId ? "" : skinId;
    setOptimisticId(newValue);
    updateSetting("activeKiwiSkinId", newValue);
  }

  return (
    <section className="space-y-2">
      <div className="flex items-center gap-2">
        <SectionHeading>Kiwi Collection</SectionHeading>
        {isSaving && (
          <div className="inline-block animate-spin rounded-full size-3 border-2 border-primary/20 border-t-primary" />
        )}
      </div>
      <p className="text-[11px] text-muted-foreground px-1">
        {skinConfigs.length}{totalCount !== undefined ? ` of ${totalCount}` : ""} collected · tap to set as logo
      </p>
      <KiwiCollectionGrid
        skins={skinConfigs}
        onSelect={handleSelect}
        activeKiwiSkinId={displayId || undefined}
      />
    </section>
  );
}

export function AnalyticsVariantA({ data, hideKiwiCollection }: AnalyticsVariantAProps) {
  const bestStreak = Math.max(...data.streaks.map((s) => s.currentStreak));

  return (
    <div className="w-full space-y-6">
      {/* ── Overview ── */}
      <section className="space-y-2">
        <SectionHeading>Overview</SectionHeading>
        <HeroSection data={data} />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          <StatCard label="Completed Today" value={formatMinutes(data.overview.today.minutes)} sub={`${data.overview.today.items} items`} icon={PlayCircle} />
          <StatCard label="Completed This Week" value={formatMinutes(data.overview.thisWeek.minutes)} sub={`${data.overview.thisWeek.items} items`} icon={Clock} />
          <StatCard label="Completed This Month" value={formatMinutes(data.overview.thisMonth.minutes)} sub={`${data.overview.thisMonth.items} items`} icon={Calendar} />
          <StatCard label="Best Streak" value={`${bestStreak} days`} icon={Flame} />
        </div>
        <div className="flex items-center justify-between text-[11px] text-muted-foreground px-1">
          <span>Active since {formatDate(data.activityBounds.firstCompletion)}</span>
          <span>Last active {formatDate(data.activityBounds.lastCompletion)}</span>
        </div>
      </section>

      <Separator />

      {/* ── Kiwi Collection ── */}
      {!hideKiwiCollection && (
        <>
          <KiwiCollectionSection />
          <Separator />
        </>
      )}

      {/* ── Activity Patterns ── */}
      <section className="space-y-2">
        <SectionHeading>Activity Patterns</SectionHeading>
        <DailyActivityChart data={data.dailyActivity} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <HourlyActivityChart data={data.hourlyActivity} />
          <DayOfWeekChart data={data.dayOfWeek} />
        </div>
      </section>

      <Separator />

      {/* ── Goals & Streaks ── */}
      <section className="space-y-2">
        <SectionHeading>Goals & Streaks</SectionHeading>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <StreaksCard data={data.streaks} />
          <GoalTrendsCard data={data.goalTrends} />
        </div>
      </section>

      <Separator />

      {/* ── Content & Sources ── */}
      <section className="space-y-2">
        <SectionHeading>Content & Sources</SectionHeading>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <ContentMixCard data={data.contentTypes} />
          <TopSourcesCard data={data.sources} />
        </div>
        <TagsCard data={data.tags} />
      </section>

      <Separator />

      {/* ── Pipeline & Scheduling ── */}
      <section className="space-y-2">
        <SectionHeading>Pipeline & Scheduling</SectionHeading>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <FunnelCard data={data.funnel} />
          <SchedulingCard data={data.scheduling} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <QueueHealthCard data={data.queue} />
          <SavedTimeCard data={data.savedTime} />
        </div>
      </section>
    </div>
  );
}
